/**
 * SessionReadyOverlay Component
 * 
 * Displays scenario information and start/cancel buttons before session begins
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface SessionReadyOverlayProps {
  title: string;
  description: string;
  objectives?: string[];
  languageMode?: 'user-preference' | 'english-only';
  onStart: (initialInputMode: 'voice' | 'keyboard' | 'ptt') => void;
  onCancel: () => void;
  // Session input mode: if false, trainees can only use voice (no keyboard/text input)
  allowKeyboardInput?: boolean;
}

const INPUT_MODE_STORAGE_KEY = 'wfvr-preferred-input-mode';

export default function SessionReadyOverlay({
  title,
  description,
  objectives,
  languageMode = 'user-preference',
  onStart,
  onCancel,
  allowKeyboardInput = true
}: SessionReadyOverlayProps) {
  const { t } = useTranslation();
  
  // Load preferred input mode from localStorage (default: voice)
  // If keyboard input is disabled by deployment settings, force voice mode
  const [inputMode, setInputMode] = useState<'voice' | 'keyboard' | 'ptt'>(() => {
    if (!allowKeyboardInput) return 'ptt';
    const stored = localStorage.getItem(INPUT_MODE_STORAGE_KEY);
    if (stored === 'keyboard' || stored === 'voice' || stored === 'ptt') return stored;
    return 'ptt'; // Default to PTT
  });
  
  // Save to localStorage when changed (only if keyboard is allowed)
  useEffect(() => {
    if (allowKeyboardInput) {
      localStorage.setItem(INPUT_MODE_STORAGE_KEY, inputMode);
    }
  }, [inputMode, allowKeyboardInput]);
  
  // Force PTT/voice mode if keyboard input is disabled
  useEffect(() => {
    if (!allowKeyboardInput && inputMode === 'keyboard') {
      setInputMode('ptt');
    }
  }, [allowKeyboardInput, inputMode]);
  
  const handleStart = () => {
    onStart(inputMode);
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
      <div className="bg-white rounded-lg shadow-2xl p-8 max-w-2xl w-full mx-4">
        <h3 className="text-2xl font-bold mb-4">{title}</h3>
        <p className="text-gray-700 mb-6">{description}</p>
        
        {/* English-only warning */}
        {languageMode === 'english-only' && (
          <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h4 className="font-semibold text-amber-900 mb-1">
                  {t('scenario.session.englishOnlyWarningTitle')}
                </h4>
                <p className="text-sm text-amber-800">
                  {t('scenario.session.englishOnlyWarningMessage')}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {objectives && objectives.length > 0 && (
          <div className="mb-6">
            <h4 className="font-semibold mb-2">{t('scenario.intro.objectivesLabel')}:</h4>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              {objectives.map((obj, idx) => (
                <li key={idx}>{obj}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Input Mode Selection - show mode options if keyboard input is allowed */}
        {allowKeyboardInput ? (
          <div className="mb-6 flex flex-col items-center gap-3">
            <label className="text-sm font-medium text-gray-700">
              {t('scenario.session.keyboard.inputModeLabel')}
            </label>
            
            <div className="inline-flex rounded-lg overflow-hidden border border-gray-200">
              {/* PTT option */}
              <button
                onClick={() => setInputMode('ptt')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  inputMode === 'ptt'
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                🎙️ {t('scenario.session.keyboard.pttMode', 'Push-to-Talk')}
              </button>
              
              {/* Voice Auto option */}
              <button
                onClick={() => setInputMode('voice')}
                className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-200 ${
                  inputMode === 'voice'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                🎤 {t('scenario.session.keyboard.voiceMode')}
              </button>
              
              {/* Keyboard option */}
              <button
                onClick={() => setInputMode('keyboard')}
                className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-200 ${
                  inputMode === 'keyboard'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                ⌨️ {t('scenario.session.keyboard.keyboardMode')}
              </button>
            </div>
          </div>
        ) : (
          /* Voice-only mode indicator (PTT default) */
          <div className="mb-6 flex flex-col items-center gap-2">
            <div className="flex items-center gap-2 text-green-600">
              <span>🎙️</span>
              <span className="text-sm font-medium">{t('scenario.session.keyboard.voiceOnlyMode')}</span>
            </div>
          </div>
        )}
        
        <div className="flex gap-3">
          <button
            onClick={handleStart}
            className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg"
          >
            {inputMode === 'keyboard' ? '⌨️' : '🎙️'} {t('scenario.intro.startSession')}
          </button>
          <button
            onClick={onCancel}
            className="px-6 py-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
          >
            {t('common.buttons.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

